import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ZoomableImageView from '../../ZoomableImage/ZoomableImageView';
import { getServerURL } from '../../../Utils/getServerURL';
import { withStyles } from 'tss-react/mui';
import useHandledQuery from '../../QueryComponents/HandledQuery/HandledQueryHook';
import SvefakturaQuery from '../../../Features/Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Components/VerifikationModal/Queries/SkannadSvefakturaQuery/EkonomiskAnalysSkannadSvefakturaQuery.gql';
import {
  EkonomiskAnalysSkannadSvefakturaQueryVariables as ISkannadSvefakturaQueryVariables,
  EkonomiskAnalysSkannadSvefakturaQuery as ISkannadSvefakturaQuery,
} from '../../../Features/Delsystem/Ekonomi/Rutiner/EkonomiskAnalys/Components/VerifikationModal/Queries/SkannadSvefakturaQuery/_QueryTypes/EkonomiskAnalysSkannadSvefakturaQuery';
import Logger from '../../../Utils/Logger';
import { CACHE_IMAGE_THRESHOLD } from '../../../Const/SystemWideConsts';
import DownloadContextMenu from './DownloadContextMenu';

interface IProps {
  bildUrls?: string[];
  centeredImage?: boolean;
  fakturaBilder: string[];
  title: string;
  showCenteredImageToggle?: boolean;
  noFullscreenToggle?: boolean;
  extraBottomPadding?: boolean;
  skannadSvefaktura: boolean;
  verifikationsNr?: string;
  fakturaId?: string | null;
  useWheelToScroll: boolean;
  classes?: any;
  ignoreFloatingMenu?: boolean;
}

const styles = {
  img: {
    objectFit: 'contain',
    objectPosition: '50% 50%',
    height: 'inherit',
    width: 'inherit',
  },
  displayMenu: {
    '&&': {
      display: 'initial !important',
    },
  },
};

export const resolveImagePromises = async (subSetOfUrls: string[]) => {
  const promises = subSetOfUrls.map(src => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.src = `${getServerURL()}/blob?blobName=SkannadSvefakturaMedBildUrl&url=${src}`;
      img.onload = () => resolve(img);
      img.onerror = () => reject(`Fel vid inhämtning av bild ${img.src}`);
    });
  });

  return Promise.all(promises);
};

const cacheImages = async (bildUrls: string[], imageIndex: number) => {
  const multipleOfThreshold = Math.floor(
    (imageIndex + 1) / CACHE_IMAGE_THRESHOLD
  );

  let subSetOfUrls: string[] = [];
  if (
    imageIndex === 0 ||
    imageIndex + 1 === multipleOfThreshold * CACHE_IMAGE_THRESHOLD
  ) {
    subSetOfUrls = bildUrls.slice(
      imageIndex + 1,
      (multipleOfThreshold + 1) * CACHE_IMAGE_THRESHOLD
    );
    return resolveImagePromises(subSetOfUrls);
  }
  return;
};

const ImgFaktura: React.FunctionComponent<
  React.PropsWithChildren<IProps>
> = props => {
  const [imageIndex, setImageIndex] = useState(0);
  const [latestPrefetchedIndex, setLatestPrefetchedIndex] = useState(
    CACHE_IMAGE_THRESHOLD - 1
  );
  const [prevFakturaId, setPrevFakturaId] = useState('');

  // ImgFaktura mountas inte om längre när man byter faktura. Sätt om imgageIndex när man byter faktura.
  if (props.fakturaId && props.fakturaId !== prevFakturaId) {
    setImageIndex(0);
    setPrevFakturaId(props.fakturaId);
  }

  const getImageSrc = useCallback(
    (fakturabildSrc: string, hasBildUrls: boolean) => {
      if (hasBildUrls) {
        return `${getServerURL()}/blob?blobName=SkannadSvefakturaMedBildUrl&url=${fakturabildSrc}`;
      } else {
        if (props.skannadSvefaktura) {
          return `${getServerURL()}${fakturabildSrc}`;
        } else {
          return `${getServerURL()}/blob?id=${props.fakturaBilder[imageIndex]}`;
        }
      }
    },
    [props.skannadSvefaktura, props.fakturaBilder, imageIndex]
  );

  const hasBildUrls = useMemo(
    () => props.bildUrls != undefined && props.bildUrls?.length > 0,
    [props.bildUrls]
  );

  //Cacha bilderna
  useEffect(() => {
    if (props.bildUrls && props.bildUrls.length > latestPrefetchedIndex) {
      const interval = setInterval(() => {
        if (hasBildUrls && props.bildUrls) {
          cacheImages(props.bildUrls, latestPrefetchedIndex)
            .then(
              () => {
                setLatestPrefetchedIndex(
                  latestPrefetchedIndex + CACHE_IMAGE_THRESHOLD
                );
              },
              rejectReason => {
                Logger.error(rejectReason);
                clearInterval(interval);
              }
            )
            .catch(error => {
              Logger.error(error);
              clearInterval(interval);
            });
        }
      }, 2000);

      return () => {
        clearInterval(interval);
      };
    }

    return;
  }, [latestPrefetchedIndex, hasBildUrls, props.bildUrls]);

  useEffect(() => {
    if (hasBildUrls && props.bildUrls) {
      cacheImages(props.bildUrls, imageIndex)
        .then(
          () => {},
          rejectReason => {
            Logger.error(rejectReason);
          }
        )
        .catch(error => {
          Logger.error(error);
        });
    }
  });

  // Om det är en skannad svefaktura vi ska visa måste vi hämta alla bilder manuellt
  const queryResult = useHandledQuery<
    ISkannadSvefakturaQuery,
    ISkannadSvefakturaQueryVariables
  >(SvefakturaQuery, {
    skip: !props.skannadSvefaktura || hasBildUrls,
    variables: {
      id: props.fakturaBilder[0],
    },
  });

  const onContextMenu: React.MouseEventHandler = useCallback(
    event => {
      if (!event.shiftKey) {
        event.preventDefault();
        const customContextMenu = document.querySelector('#customContextMenu');

        const targetBoundingRect = event.currentTarget.getBoundingClientRect();

        if (customContextMenu) {
          customContextMenu.classList.add(props.classes.displayMenu);
          const menuRect = customContextMenu.getBoundingClientRect();

          const left =
            event.pageX + menuRect.width > targetBoundingRect.right
              ? event.pageX - targetBoundingRect.x - menuRect.width
              : event.pageX - targetBoundingRect.x;

          const top =
            event.pageY + menuRect.height > targetBoundingRect.bottom
              ? event.pageY - targetBoundingRect.y - menuRect.height
              : event.pageY - targetBoundingRect.y;

          customContextMenu.setAttribute(
            'style',
            `top:${top}px;left:${left}px;`
          );

          const bodyClickListener: (
            this: HTMLElement,
            ev: MouseEvent
          ) => any = event => {
            const menuRect = customContextMenu.getBoundingClientRect();

            if (
              event.pageX < menuRect.left ||
              event.pageX > menuRect.right ||
              event.pageY < menuRect.top ||
              event.pageY > menuRect.bottom
            ) {
              customContextMenu.classList.remove(props.classes.displayMenu);
              customContextMenu.setAttribute('style', '');
              document.body.removeEventListener('click', bodyClickListener);
            }
          };

          document.body.addEventListener('click', bodyClickListener);
        }
      }
    },
    [props.classes.displayMenu]
  );

  if (
    props.skannadSvefaktura &&
    !hasBildUrls &&
    (!queryResult.data ||
      queryResult.data.ek_skannad_svefaktura_bilder.length <= 0)
  ) {
    return queryResult.renderComponent;
  }

  let fakturaBilder: string[] = [];
  if (hasBildUrls) {
    fakturaBilder = props.bildUrls ?? [];
  } else {
    fakturaBilder =
      props.skannadSvefaktura && queryResult.data
        ? queryResult.data.ek_skannad_svefaktura_bilder
        : props.fakturaBilder;
  }

  return (
    <>
      <ZoomableImageView
        imageCount={fakturaBilder.length}
        imageIndex={imageIndex}
        isImage
        nextImage={() =>
          setImageIndex(Math.min(imageIndex + 1, fakturaBilder.length - 1))
        }
        previousImage={() => setImageIndex(Math.max(imageIndex - 1, 0))}
        title={props.title}
        subtitle="FAKTURABILD"
        showCenteredImageToggle={props.showCenteredImageToggle}
        noFullscreenToggle={props.noFullscreenToggle}
        extraBottomPadding={props.extraBottomPadding}
        navHeader="Fakturabild"
        centeredImage={props.centeredImage}
        onContextMenu={onContextMenu}
        useWheelToScroll={props.useWheelToScroll}
        ignoreFloatingMenu={props.ignoreFloatingMenu}
      >
        <img
          className={props.classes.img}
          id="Fakturabild"
          src={getImageSrc(fakturaBilder[imageIndex], hasBildUrls)}
          alt="Fakturabild"
        />
      </ZoomableImageView>
      <DownloadContextMenu
        contextMenuID="customContextMenu"
        displayMenuClass={props.classes.displayMenu}
        imageIndex={imageIndex}
        imageSrc={`${getServerURL()}/blob?id=${
          props.fakturaBilder[imageIndex]
        }`}
        isSveFaktura={props.skannadSvefaktura && hasBildUrls}
        verifikationsNr={props.verifikationsNr}
      />
    </>
  );
};

export default withStyles(ImgFaktura, styles);
